import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { catchError, of } from 'rxjs';
import { BriefService } from '@/app/services/brief/brief.service';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
} from '@/app/common/general-types/briefs.dto';
import { ApplicationStatusBadgeComponent } from '@/app/atoms/application-status-badge/application-status-badge.component';
import { CreatorApplicationDrawer } from '@/app/pages/insights/tabs/creator/molecules/application-drawer/application-drawer';
import { ApplicantsFilter } from '@/app/common/general-types/filters.dto';

export type BriefApplicationStatusFilter = 'all' | 'invited' | 'applied' | 'hired' | 'completed';

const STATUS_FILTERS: { key: BriefApplicationStatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'invited', label: 'Invited' },
  { key: 'applied', label: 'Applied' },
  { key: 'hired', label: 'Hired' },
  { key: 'completed', label: 'Completed' },
];

const STATUS_FILTER_MAP: Record<
  Exclude<BriefApplicationStatusFilter, 'all'>,
  ApplicationStatusEnum[]
> = {
  invited: [ApplicationStatusEnum.Invited],
  applied: [ApplicationStatusEnum.Applied],
  hired: [ApplicationStatusEnum.Hired],
  completed: [ApplicationStatusEnum.Completed],
};

@Component({
  selector: 'ins-brief-applications',
  imports: [DatePipe, ApplicationStatusBadgeComponent, CreatorApplicationDrawer],
  templateUrl: './applications.html',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefApplications implements OnInit {
  @ViewChild('applicationDrawer') applicationDrawer?: CreatorApplicationDrawer;

  readonly statusFilters = STATUS_FILTERS;
  readonly selectedStatus = signal<BriefApplicationStatusFilter>('all');
  readonly applications = signal<BriefApplicationResponseDto[] | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  private briefId = signal<string | undefined>(undefined);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private briefService = inject(BriefService);

  ngOnInit(): void {
    const id = this.route.parent?.snapshot.paramMap.get('briefId') ?? undefined;
    this.briefId.set(id);
    if (id) {
      this.loadApplications();
    }
  }

  selectStatus(status: BriefApplicationStatusFilter): void {
    this.selectedStatus.set(status);
    this.loadApplications();
  }

  statusFilterClass(status: BriefApplicationStatusFilter): string {
    return this.selectedStatus() === status
      ? 'bg-primary/10 text-accent ring-1 ring-primary/30'
      : 'text-secondary hover:bg-zinc-100 dark:hover:bg-zinc-800';
  }

  openApplication(application: BriefApplicationResponseDto): void {
    this.applicationDrawer?.open(application);
  }

  onApplicationUpdated(application: BriefApplicationResponseDto): void {
    this.applications.update((list) =>
      (list ?? []).map((item) => (item.id === application.id ? { ...item, ...application } : item)),
    );
  }

  creatorName(application: BriefApplicationResponseDto): string {
    const creator = application.creator;
    if (creator) {
      const name = [creator.firstName, creator.lastName].filter(Boolean).join(' ').trim();
      return name || creator.email || 'Unknown creator';
    }
    const fallback = application.creatorUserId;
    return fallback || 'Unknown creator';
  }

  creatorSubtitle(application: BriefApplicationResponseDto): string {
    return application.creator?.email || application.creatorUserId || '—';
  }

  private loadApplications(): void {
    const briefId = this.briefId();
    if (!briefId) {
      return;
    }

    const status = this.selectedStatus();
    const filters: ApplicantsFilter = {
      skip: 0,
      take: 100,
      sortField: 'applicationStatusModifiedDate',
      sortOrder: 'DESC',
    };

    if (status !== 'all') {
      filters.applicationStatus = STATUS_FILTER_MAP[status];
    }

    this.loadingState.set('loading');
    this.briefService
      .getApplicants(briefId, filters)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of({ count: 0, data: [] as BriefApplicationResponseDto[] });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.applications.set(response.data ?? []);
        this.loadingState.set('loaded');
      });
  }
}
